import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import TurnstileWidget from "@/components/TurnstileWidget";
import { roleHome } from "@/components/auth/roleHome";
import { TwoFAForm } from "@/components/auth/TwoFAForm";
import { SignupIdentityFields, SignupExtraFields } from "@/components/auth/SignupFields";
import { DocumentChecklistStandard } from "@/components/DocumentChecklistCard";

const FAMILY_IMG =
  "https://images.unsplash.com/photo-1579538506280-84e461ad0077?crop=entropy&cs=srgb&fm=jpg&w=1200&q=75";

export default function AuthPage({ mode = "login" }) {
  const { user, login, register, verifyTwoFA } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRef = (params.get("ref") || "").toUpperCase();
  const initialAttorney = params.get("attorney") === "1";
  const [form, setForm] = useState({
    email: "", password: "", name: "", invite_code: "", referral_code: initialRef,
    role: initialAttorney ? "attorney" : "user", firm_name: "", bar_number: "", bar_state: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteRequired, setInviteRequired] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [twofa, setTwofa] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");

  useEffect(() => {
    api.get("/auth/access-config")
      .then((r) => {
        setInviteRequired(!!r.data?.invite_required);
        setCaptchaRequired(!!r.data?.captcha_required);
        setTurnstileSiteKey(r.data?.turnstile_site_key || "");
      })
      .catch(() => { setInviteRequired(false); setCaptchaRequired(false); });
  }, []);

  const handleTurnstile = useCallback((token) => setTurnstileToken(token || ""), []);

  if (user === null) return null; // bootstrapping
  if (user) {
    // Route each role to their home surface (admin/attorney/affiliate/client).
    return <Navigate to={roleHome(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // If captcha is required by the backend AND site key is set, block until token.
    if (captchaRequired && turnstileSiteKey && !turnstileToken) {
      setErr("Please complete the spam check before continuing.");
      return;
    }
    setLoading(true);
    const res = mode === "login"
      ? await login(form.email, form.password, turnstileToken)
      : await register(form.email, form.password, form.name, form.invite_code, form.referral_code, turnstileToken, {
          role: form.role,
          firm_name: form.firm_name,
          bar_number: form.bar_number,
          bar_state: form.bar_state,
        });
    setLoading(false);
    if (res.ok) {
      nav(roleHome(res.user?.role));
    } else if (res.twofa) {
      setTwofa(res.twofa);
    } else setErr(friendlyError({ message: res.error }, res.error));
  };

  const submitTwofa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await verifyTwoFA(twofa.challenge_id, twofaCode.trim());
    setLoading(false);
    if (res.ok) {
      nav(roleHome(res.user?.role));
    } else {
      setErr(res.error || "Verification failed.");
      if (["expired", "already_used", "too_many_attempts", "challenge_not_found"].includes(res.reason)) {
        setTwofa(null);
        setTwofaCode("");
      }
    }
  };

  const isLogin = mode === "login";

  const submitLabel = (() => {
    if (loading) return "Please wait…";
    return isLogin ? "Sign in" : "Create account";
  })();

  return (
    <AppShell showNav={false}>
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div className="hidden md:block">
            <div className="pill pill-ai mb-5">
              <span>·</span> Available in multiple languages
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] font-extrabold mb-5"
                style={{ color: "var(--text-primary)" }}>
              Prepare your family-based<br/>
              <span style={{ color: "var(--action)" }}>DS-260</span> case with confidence.
            </h1>
            <p className="text-[15px] leading-relaxed mb-8" style={{ color: "var(--text-secondary)" }}>
              Speak any of 15 languages, upload your documents, and let the assistant guide you
              step-by-step through every family member's DS-260 — the same way an experienced professional would.
            </p>
            <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--border-stone)" }}>
              <img src={FAMILY_IMG} alt="family" className="w-full h-72 object-cover" />
            </div>
            {!isLogin && (
              <div className="mt-6">
                <DocumentChecklistStandard />
              </div>
            )}
          </div>

          <div>
            {!isLogin && (
              <div className="md:hidden mb-6">
                <DocumentChecklistStandard testid="doc-checklist-standard-mobile" />
              </div>
            )}
          <div className="card-surface p-8 sm:p-10 anim-in" data-testid="auth-card">
            <h2 className="font-display text-2xl font-bold mb-1">
              {isLogin ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-sm mb-7" style={{ color: "var(--text-secondary)" }}>
              {isLogin
                ? "Continue preparing your family-based case."
                : "Set up in 30 seconds and start your case."}
            </p>

            {twofa ? (
            <TwoFAForm
              twofa={twofa}
              code={twofaCode}
              onCodeChange={setTwofaCode}
              err={err}
              loading={loading}
              onSubmit={submitTwofa}
              onBack={() => { setTwofa(null); setTwofaCode(""); setErr(""); }}
            />
          ) : (
          <form onSubmit={submit} className="space-y-4">
              {!isLogin && (
                <SignupIdentityFields
                  form={form}
                  setForm={setForm}
                  inviteRequired={inviteRequired}
                  onAffiliateClick={() => nav("/affiliate")}
                />
              )}
              <div>
                <label className="field-label">Email</label>
                <input
                  data-testid="auth-email-input"
                  type="email"
                  className="field-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="field-label">Password</label>
                  {isLogin && (
                    <Link
                      to="/forgot-password"
                      className="text-xs font-semibold hover:underline"
                      style={{ color: "var(--accent-copper)" }}
                      data-testid="auth-forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
                <input
                  data-testid="auth-password-input"
                  type="password"
                  className="field-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
              </div>

              {!isLogin && <SignupExtraFields form={form} setForm={setForm} initialRef={initialRef} />}

              {err && (
                <div className="text-sm px-3 py-2 rounded-md"
                     style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}
                     data-testid="auth-error">
                  {err}
                </div>
              )}

              {captchaRequired && turnstileSiteKey && (
                <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstile} />
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-2"
              >
                {submitLabel}
              </button>
            </form>
          )}

            <div className="mt-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              {!isLogin && (
                <p className="text-xs mb-3" data-testid="register-save-resume-hint">
                  ✓ Free to start · ✓ Progress auto-saves · ✓ Come back whenever
                </p>
              )}
              {isLogin ? (
                <>New here?{" "}
                  <Link to="/register" className="font-semibold" style={{ color: "var(--action)" }} data-testid="switch-to-register">
                    Create an account
                  </Link>
                </>
              ) : (
                <>Already registered?{" "}
                  <Link to="/login" className="font-semibold" style={{ color: "var(--action)" }} data-testid="switch-to-login">
                    Sign in
                  </Link>
                </>
              )}
            </div>

            {/* Demo credentials removed 2026-02-14 per security audit SEC-002 —
                do NOT reintroduce; see /app/memory/PRD.md for context. */}
          </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
